import { useEffect, useState, useCallback } from "react";
import { TradeAccount } from "@/lib/stores/userTradeAccounts";
import { useAccountInfoStore } from "@/lib/stores/accountInfoStore";

// Cache for API responses
interface CacheEntry<T> {
  data: T;
  timestamp: number;
}

// Cache duration in milliseconds (10 seconds)
const CACHE_DURATION = 10000;

// Cache objects
const balancesCache: Record<string, CacheEntry<BinanceFuturesBalance[]>> = {};
const accountInfoCache: Record<string, CacheEntry<BinanceAccountInfo>> = {};

interface BinanceFuturesBalance {
  accountAlias: string;
  asset: string;
  balance: string;
  crossWalletBalance: string;
  crossUnPnl: string;
  availableBalance: string;
  maxWithdrawAmount: string;
  marginAvailable: boolean;
  updateTime: number;
}

interface BinanceBalanceError {
  code: number;
  msg: string;
}

interface UseBinanceBalancesResult {
  balances: BinanceFuturesBalance[];
  loading: boolean;
  error: string | null;
  refetch: () => Promise<void>;
}

export function useBinanceBalances(
  account: TradeAccount | null,
): UseBinanceBalancesResult {
  const [balances, setBalances] = useState<BinanceFuturesBalance[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBalances = useCallback(async () => {
    if (!account?.apiKey || !account?.apiSecret) {
      setBalances([]);
      return;
    }

    // Generate cache key based on account API key
    const cacheKey = account.apiKey;
    const now = Date.now();

    // Check if we have a valid cache entry
    if (
      balancesCache[cacheKey] &&
      now - balancesCache[cacheKey].timestamp < CACHE_DURATION
    ) {
      // Use cached data
      setBalances(balancesCache[cacheKey].data);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const timestamp = now;
      const queryString = `timestamp=${timestamp}`;

      // Create signature using the account's API secret
      const signature = await createHmacSignature(
        queryString,
        account.apiSecret,
      );

      // Use testnet endpoint if account.testnet is true
      const baseUrl = account?.testnet
        ? "https://testnet.binancefuture.com"
        : "https://fapi.binance.com";

      const response = await fetch(
        `${baseUrl}/fapi/v3/balance?${queryString}&signature=${signature}`,
        {
          headers: {
            "X-MBX-APIKEY": account.apiKey,
          },
        },
      );

      if (!response.ok) {
        const errorData: BinanceBalanceError = await response.json();
        throw new Error(`Binance API error: ${errorData.msg}`);
      }

      const data: BinanceFuturesBalance[] = await response.json();

      // Update cache
      balancesCache[cacheKey] = {
        data,
        timestamp: now,
      };

      setBalances(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch balances");
    } finally {
      setLoading(false);
    }
  }, [account?.apiKey, account?.apiSecret, account?.testnet]);

  useEffect(() => {
    fetchBalances();

    // Set up an interval to refresh balances using the cache duration
    const intervalId = setInterval(fetchBalances, CACHE_DURATION);

    return () => clearInterval(intervalId);
  }, [account?.apiKey, account?.apiSecret, account?.testnet, fetchBalances]);

  return {
    balances,
    loading,
    error,
    refetch: fetchBalances,
  };
}

async function createHmacSignature(
  queryString: string,
  apiSecret: string,
): Promise<string> {
  const encoder = new TextEncoder();
  const key = encoder.encode(apiSecret);
  const message = encoder.encode(queryString);

  const cryptoKey = await crypto.subtle.importKey(
    "raw",
    key,
    { name: "HMAC", hash: "SHA-256" },
    false,
    ["sign"],
  );

  const signature = await crypto.subtle.sign("HMAC", cryptoKey, message);
  return Array.from(new Uint8Array(signature))
    .map((b) => b.toString(16).padStart(2, "0"))
    .join("");
}

export interface BinanceAccountInfo {
  feeTier: number;
  canTrade: boolean;
  canDeposit: boolean;
  canWithdraw: boolean;
  updateTime: number;
  totalInitialMargin: string;
  totalMaintMargin: string;
  totalWalletBalance: string;
  totalUnrealizedProfit: string;
  totalMarginBalance: string;
  totalPositionInitialMargin: string;
  totalOpenOrderInitialMargin: string;
  totalCrossWalletBalance: string;
  availableBalance: string;
  maxWithdrawAmount: string;
}

export function useBinanceAccount(account: TradeAccount | null) {
  // Get store methods
  const {
    getAccountInfo,
    isLoading,
    getError,
    setAccountInfo: storeSetAccountInfo,
    setLoading: storeSetLoading,
    setError: storeSetError,
  } = useAccountInfoStore();

  const fetchAccountInfo = useCallback(async () => {
    if (
      !account?.id ||
      !account?.apiKey ||
      !account?.apiSecret ||
      account.exchange !== "BINANCE" ||
      account.exchangeType !== "PERPETUAL"
    ) {
      storeSetAccountInfo(account?.id || "unknown", null);
      return;
    }

    // Generate cache key based on account API key
    const cacheKey = account.apiKey;
    const now = Date.now();

    // Check if we have a valid cache entry
    if (
      accountInfoCache[cacheKey] &&
      now - accountInfoCache[cacheKey].timestamp < CACHE_DURATION
    ) {
      // Use cached data
      storeSetAccountInfo(account.id, accountInfoCache[cacheKey].data);
      return;
    }

    storeSetLoading(account.id, true);
    storeSetError(account.id, null);

    try {
      const timestamp = now;
      const queryString = `timestamp=${timestamp}`;

      const signature = await createHmacSignature(
        queryString,
        account.apiSecret,
      );

      // Use testnet endpoint if account.testnet is true
      const baseUrl = account?.testnet
        ? "https://testnet.binancefuture.com"
        : "https://fapi.binance.com";

      const response = await fetch(
        `${baseUrl}/fapi/v3/account?${queryString}&signature=${signature}`,
        {
          headers: {
            "X-MBX-APIKEY": account.apiKey,
          },
        },
      );

      if (!response.ok) {
        const errorData: BinanceBalanceError = await response.json();
        throw new Error(`Binance API error: ${errorData.msg}`);
      }

      const data: BinanceAccountInfo = await response.json();

      // Update cache
      accountInfoCache[cacheKey] = {
        data,
        timestamp: now,
      };

      storeSetAccountInfo(account.id, data);
    } catch (err) {
      storeSetError(
        account.id,
        err instanceof Error ? err.message : "Failed to fetch account info",
      );
    } finally {
      storeSetLoading(account.id, false);
    }
  }, [
    account?.id,
    account?.apiKey,
    account?.apiSecret,
    account?.exchange,
    account?.exchangeType,
    account?.testnet,
    storeSetAccountInfo,
    storeSetLoading,
    storeSetError,
  ]);

  useEffect(() => {
    if (account?.id) {
      fetchAccountInfo();

      // Set up an interval to refresh account info using the cache duration
      const intervalId = setInterval(fetchAccountInfo, CACHE_DURATION);

      return () => clearInterval(intervalId);
    }
  }, [fetchAccountInfo, account?.id]);

  return {
    accountInfo: account?.id ? getAccountInfo(account.id) : null,
    loading: account?.id ? isLoading(account.id) : false,
    error: account?.id ? getError(account.id) : null,
    refetch: fetchAccountInfo,
  };
}
